import { getLocalStorageItem, setLocalStorageItem } from '@snapchat/mw-common';
import noop from 'lodash-es/noop';
import { useCallback, useContext, useEffect, useState } from 'react';

import { AppContext } from '../../AppContext';
import { Config } from '../../config';
import { logEvent, SubscribedEventType } from '../../helpers/logging';
import { UserAction } from '../../types/events';
import type { EcommerceEventProduct } from '../../types/gtm';
import type { BasicCart, CartEvent, CartState } from './types';

const shopifyCartKey = 'mwp-shopify-cart';

export const initialCartState: CartState = {
  lineItems: {},
  cartOpen: false,
  addOne: noop,
  removeOne: noop,
  setCartOpen: noop,
  discountCode: '',
  setDiscountCode: noop,
  sellingPlanSelections: {},
  setSellingPlan: noop,
};

const getUrlDiscount = (urlString: string) => {
  try {
    const url = new URL(urlString);
    return url.searchParams.get('discount') ?? '';
  } catch {
    return '';
  }
};

export const useCart = (): CartState => {
  const appContext = useContext(AppContext);
  const country = appContext.userLocation.country;

  // Seed discount code from URL query param (e.g. ?discount=SAVE10)
  const urlDiscount = getUrlDiscount(appContext.getCurrentUrl());

  const [cart, setCart] = useState<CartState>(initialCartState);
  const [discountCode, setDiscountCode] = useState(urlDiscount);
  const [sellingPlanSelections, setSellingPlanSelections] = useState<Record<string, string>>({});

  const setSellingPlan = useCallback((variantId: string, sellingPlanId: string) => {
    setSellingPlanSelections(prev => ({ ...prev, [variantId]: sellingPlanId }));
  }, []);

  const setCartOpen = (cartOpen: boolean) => {
    if (cartOpen) {
      logEvent({
        eventCategory: 'Cart',
        eventAction: UserAction.Click,
        eventLabel: 'Open Cart',
        subscribedEventType: SubscribedEventType.USER_INTERACTION,
      });
    } else {
      logEvent({
        eventCategory: 'Cart',
        eventAction: UserAction.Click,
        eventLabel: 'Close Cart',
        subscribedEventType: SubscribedEventType.USER_INTERACTION,
      });
    }

    setCart({ ...cart, cartOpen });
  };

  const addOne = useCallback(
    ({ handle, variantId, contentfulProduct, shopifyProducts, currencyCode }: CartEvent) => {
      const newLineItems = { ...cart.lineItems };
      const shopifyProduct = shopifyProducts[handle]!;
      const ecommerceEventProducts: EcommerceEventProduct[] = [];

      // Use variantId as cart key (falls back to first variant if not provided)
      const cartKey = variantId || shopifyProduct.id;
      const productInCart = newLineItems[cartKey];

      newLineItems[cartKey] = (productInCart ?? 0) + 1;

      const selectedVariant = shopifyProduct.variants?.find(v => v.id === cartKey);
      const productTitle = contentfulProduct?.title ?? shopifyProduct.title;

      ecommerceEventProducts.push({
        name: productTitle,
        id: selectedVariant?.sku ?? shopifyProduct.sku,
        variant: selectedVariant?.title ?? shopifyProduct.title,
        price: (selectedVariant?.price ?? shopifyProduct.price).toString(),
        shopifyid: cartKey,
        quantity: 1,
      });

      const bundleItems = contentfulProduct?.bundleItemsCollection?.items ?? [];

      // TODO: Guard against missing bundle products — shopifyProducts only has first 25 products,
      // so a bundle item could reference a product not in the map. Add `if (!bundleShopifyProduct) continue;`
      // when bundles are actively used. See PR #4363 discussion_r6691799.
      for (const { title, productHandle } of bundleItems) {
        const bundleShopifyProduct = shopifyProducts[productHandle]!;
        const bundleVariantId = bundleShopifyProduct?.variants?.[0]?.id ?? bundleShopifyProduct?.id;
        const bundleItemFromCart = newLineItems[bundleVariantId] ?? 0;

        newLineItems[bundleVariantId] = bundleItemFromCart + 1;

        ecommerceEventProducts.push({
          name: title,
          id: bundleShopifyProduct.sku,
          variant: bundleShopifyProduct.title,
          price: bundleShopifyProduct.price.toString(),
          shopifyid: bundleVariantId,
          quantity: 1,
        });
      }

      setLocalStorageItem(shopifyCartKey, JSON.stringify({ lineItems: newLineItems }));

      logEvent({
        eventCategory: 'Cart',
        eventAction: 'Add',
        eventLabel: ecommerceEventProducts.map(product => product.shopifyid).join(','),
        numberOfProducts: ecommerceEventProducts.length,
        ecommerce: {
          currencyCode,
          add: {
            products: ecommerceEventProducts,
          },
        },
        subscribedEventType: SubscribedEventType.ECOMMERCE,
      });

      setCart({
        ...cart,
        lineItems: newLineItems,
        cartOpen: true,
      });
    },
    [cart]
  );

  const removeOne = useCallback(
    ({ handle, variantId, contentfulProduct, shopifyProducts, currencyCode }: CartEvent) => {
      const newLineItems = { ...cart.lineItems };
      const shopifyProduct = shopifyProducts[handle]!;
      const ecommerceEventProducts: EcommerceEventProduct[] = [];

      // Use variantId as cart key (falls back to first variant if not provided)
      const cartKey = variantId || shopifyProduct.id;
      const productInCart = newLineItems[cartKey] ?? 0;

      if (productInCart > 0) {
        const newQty = productInCart - 1;

        if (newQty === 0) {
          delete newLineItems[cartKey];
        } else {
          newLineItems[cartKey] = newQty;
        }
      }

      const selectedVariant = shopifyProduct.variants?.find(v => v.id === cartKey);

      ecommerceEventProducts.push({
        name: contentfulProduct?.title ?? selectedVariant?.title ?? shopifyProduct.title,
        id: selectedVariant?.sku ?? shopifyProduct.sku,
        variant: selectedVariant?.title ?? shopifyProduct.title,
        price: (selectedVariant?.price ?? shopifyProduct.price).toString(),
        shopifyid: cartKey,
        quantity: 1,
      });

      const bundleItems = contentfulProduct?.bundleItemsCollection?.items ?? [];

      // TODO: Same guard as addOne — see PR #4363 discussion_r6691799.
      for (const { productHandle } of bundleItems) {
        const bundleShopifyProduct = shopifyProducts[productHandle]!;
        const bundleVariantId = bundleShopifyProduct?.variants?.[0]?.id ?? bundleShopifyProduct?.id;
        const bundleItemFromCart = newLineItems[bundleVariantId] ?? 0;

        if (bundleItemFromCart > 0) {
          const newBundleQty = bundleItemFromCart - 1;

          if (newBundleQty === 0) {
            delete newLineItems[bundleVariantId];
          } else {
            newLineItems[bundleVariantId] = newBundleQty;
          }
        }

        ecommerceEventProducts.push({
          name: bundleShopifyProduct.title,
          id: bundleShopifyProduct.sku,
          variant: bundleShopifyProduct.title,
          price: bundleShopifyProduct.price.toString(),
          shopifyid: bundleVariantId,
          quantity: 1,
        });
      }

      setLocalStorageItem(shopifyCartKey, JSON.stringify({ lineItems: newLineItems }));

      logEvent({
        eventCategory: 'Cart',
        eventAction: 'Remove',
        eventLabel: ecommerceEventProducts.map(product => product.shopifyid).join(','),
        currencyCode,
        productIds: ecommerceEventProducts.map(product => product.shopifyid).join(','),
        ecommerce: {
          remove: {
            products: ecommerceEventProducts,
          },
        },
        subscribedEventType: SubscribedEventType.ECOMMERCE,
      });

      setCart({
        ...cart,
        lineItems: newLineItems,
      });
    },
    [cart]
  );

  const cartInit = useCallback(() => {
    const initialCartString: string | BasicCart | null = getLocalStorageItem(shopifyCartKey);
    let initialCart: BasicCart;

    if (initialCartString) {
      initialCart = JSON.parse(initialCartString) as BasicCart;
    } else {
      initialCart = {
        lineItems: {},
      } as BasicCart;

      setLocalStorageItem(shopifyCartKey, JSON.stringify(initialCart));
    }

    setCart(currentCart => ({
      ...currentCart,
      ...(initialCart as BasicCart),
      cartOpen: false,
    }));
  }, []);

  useEffect(() => {
    if (!Config.shopify) {
      return;
    }

    cartInit();
  }, [country, cartInit]);

  return {
    ...cart,
    addOne,
    removeOne,
    setCartOpen,
    discountCode,
    setDiscountCode,
    sellingPlanSelections,
    setSellingPlan,
  };
};
